import React from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { CheckCircle as CheckCircleIcon, ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material';
import StarRating from '../StarRating/StarRating';

const DESIGN_TOKENS = {
  colors: {
    text: {
      primary: '#181818',
      secondary: '#93989C',
    },
    background: {
      white: '#FFFFFF',
    },
    border: {
      light: '#F1F4F4',
    },
    indicator: {
      new: '#426A53',
    },
  },
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
  },
  borderRadius: {
    indicator: '50%',
  },
  typography: {
    body: {
      fontSize: '16px',
      lineHeight: '1.4',
      fontWeight: 400,
    },
    small: {
      fontSize: '13px',
      lineHeight: '1.4',
      fontWeight: 400,
    },
  },
};

export interface NotificationItemProps {
  id: string;
  type: 'order' | 'review' | 'question' | 'other';
  title: string;
  description: string;
  timestamp: string;
  isNew: boolean;
  isUnread?: boolean;
  rating?: number;
  onMarkAsRead?: (id: string) => void;
  onClick?: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  id,
  title,
  description,
  timestamp,
  isNew,
  isUnread,
  rating,
  onMarkAsRead,
  onClick,
}) => {
  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering onClick when clicking the mark-as-read button
    if (onMarkAsRead && isUnread) {
      onMarkAsRead(id);
    }
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const isClickable = !!onClick;

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        gap: DESIGN_TOKENS.spacing.md,
        padding: `${DESIGN_TOKENS.spacing.md} 0`,
        borderBottom: `1px solid ${DESIGN_TOKENS.colors.border.light}`,
        position: 'relative',
        cursor: isClickable ? 'pointer' : 'default',
        borderRadius: isClickable ? '8px' : '0',
        marginX: isClickable ? '-8px' : '0',
        paddingX: isClickable ? '8px' : '0',
        '&:hover': isClickable ? {
          backgroundColor: 'rgba(103, 49, 79, 0.04)',
          transform: 'translateX(4px)',
        } : {},
        transition: 'all 0.2s ease',
        '&:last-child': {
          borderBottom: 'none',
        },
      }}
    >
      {/* Unread indicator - green check icon on the left (clickable to mark as read) */}
      {isUnread && (
        <Tooltip title="Mark as read">
          <IconButton
            onClick={handleMarkAsRead}
            size="small"
            sx={{
              position: 'absolute',
              left: '-32px',
              top: '13px',
              padding: '4px',
              color: '#426A53',
              zIndex: 1, // Ensure it's above the clickable area
              '&:hover': {
                color: '#426A53',
                backgroundColor: 'rgba(66, 106, 83, 0.08)',
                transform: 'scale(1.1)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
      
      {/* New indicator dot */}
      {isNew && !isUnread && (
        <Box
          sx={{
            position: 'absolute',
            left: '-20px',
            top: '23px',
            width: '8px',
            height: '8px',
            backgroundColor: DESIGN_TOKENS.colors.indicator.new,
            borderRadius: DESIGN_TOKENS.borderRadius.indicator,
          }}
        />
      )}

      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: DESIGN_TOKENS.spacing.xs,
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: DESIGN_TOKENS.typography.body.fontSize,
            lineHeight: DESIGN_TOKENS.typography.body.lineHeight,
            fontWeight: DESIGN_TOKENS.typography.body.fontWeight,
            color: DESIGN_TOKENS.colors.text.primary,
          }}
        >
          {title}
        </Typography>

        {rating !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: DESIGN_TOKENS.spacing.sm }}>
            <StarRating rating={rating} size="small" showNumber />
          </Box>
        )}

        <Typography
          sx={{
            fontSize: DESIGN_TOKENS.typography.body.fontSize,
            lineHeight: DESIGN_TOKENS.typography.body.lineHeight,
            fontWeight: DESIGN_TOKENS.typography.body.fontWeight,
            color: DESIGN_TOKENS.colors.text.primary,
            whiteSpace: 'pre-line',
          }}
        >
          {description}
        </Typography>
      </Box>

      {/* Timestamp and Click Indicator */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: DESIGN_TOKENS.spacing.sm,
        }}
      >
        <Typography
          sx={{
            fontSize: DESIGN_TOKENS.typography.small.fontSize,
            lineHeight: DESIGN_TOKENS.typography.small.lineHeight,
            fontWeight: DESIGN_TOKENS.typography.small.fontWeight,
            color: isNew ? DESIGN_TOKENS.colors.text.primary : DESIGN_TOKENS.colors.text.secondary,
            whiteSpace: 'nowrap',
          }}
        >
          {timestamp}
        </Typography>
        {isClickable && (
          <ArrowForwardIosIcon
            sx={{
              fontSize: '14px',
              color: DESIGN_TOKENS.colors.text.secondary,
              opacity: 0.6,
              transition: 'all 0.2s ease',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default NotificationItem;


